// Layer 3 (Transformation): plant-local time arithmetic — every SeriesTime field
// is treated as UTC (Date.UTC) so host zones never leak into captures.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { PLANT_CALENDAR } from '@/versions/v1/schema/reading-series';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EPOCH_MS_BY_STAMP = new Map<SeriesTime, number>();

function daysSinceWeekEpoch(date: string): number {
  const epoch = parseTime(`${PLANT_CALENDAR.weekEpoch}T12:00`);
  const d = parseTime(`${date}T12:00`);
  return Math.round((d.getTime() - epoch.getTime()) / 86_400_000);
}

/** Case and action stamps are fixed SeriesTime values after uptake-seed #302. */
export function asCaseTime(stamp: string): SeriesTime {
  if (!stamp.includes('T')) {
    throw new Error(`Expected dated SeriesTime, got "${stamp}"`);
  }
  return stamp as SeriesTime;
}

/** Cache immutable epoch arithmetic while returning a fresh Date for callers
 * that mutate their result (for example weekStart/monthStart). */
export function timeMilliseconds(t: SeriesTime): number {
  if (EPOCH_MS_BY_STAMP.has(t)) return EPOCH_MS_BY_STAMP.get(t)!;
  const [datePart, timePart] = t.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart ?? '00:00').split(':').map(Number);
  const epochMs = Date.UTC(y, m - 1, d, hh, mm);
  EPOCH_MS_BY_STAMP.set(t, epochMs);
  return epochMs;
}

export function parseTime(t: SeriesTime): Date {
  return new Date(timeMilliseconds(t));
}

export function formatTime(d: Date): SeriesTime {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

export function addMinutes(t: SeriesTime, minutes: number): SeriesTime {
  const d = parseTime(t);
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return formatTime(d);
}

export function diffMinutes(a: SeriesTime, b: SeriesTime): number {
  return (timeMilliseconds(a) - timeMilliseconds(b)) / 60_000;
}

/** "2h 15m" / "45m" / "3h" — never a raw minute count or a decimal hour.
 *  No clamp: every call site (`reports/Scene.transform.ts`, `overview/
 *  Uptake.transform.ts`) already guards its input `>= 0` before calling this
 *  — a negative here would be a caller bug, and a silent `Math.max(0, …)`
 *  would hide it behind a misleading `0m` instead of throwing it into view
 *  (fixer round after the uptake review, SHOULD-FIX 8). */
export function formatElapsedMinutes(minutes: number): string {
  const whole = Math.round(minutes);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function clamp(t: SeriesTime, min: SeriesTime, max: SeriesTime): SeriesTime {
  if (diffMinutes(t, min) < 0) return min;
  if (diffMinutes(t, max) > 0) return max;
  return t;
}

/** Plant weekday Mon = 1 … Sun = 7 — deck calendar via `weekEpoch`, not civil ISO. */
export function isoWeekday(t: SeriesTime): number {
  const { date } = productionDayOf(t);
  const days = daysSinceWeekEpoch(date);
  return ((days % 7) + 7) % 7 + 1;
}

export function daysInMonth(t: SeriesTime): number {
  const d = parseTime(t);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}

export function shiftOf(at: SeriesTime): {
  kind: 'day' | 'night';
  start: SeriesTime;
  end: SeriesTime;
} {
  const d = parseTime(at);
  const hh = d.getUTCHours();
  const mm = d.getUTCMinutes();
  const minutes = hh * 60 + mm;
  const dayStart = 6 * 60;
  const nightStart = 18 * 60;
  const date = formatTime(d).split('T')[0]!;

  if (minutes >= dayStart && minutes < nightStart) {
    return {
      kind: 'day',
      start: `${date}T${PLANT_CALENDAR.dayShiftStart}`,
      end: `${date}T${PLANT_CALENDAR.nightShiftStart}`,
    };
  }

  if (minutes >= nightStart) {
    return {
      kind: 'night',
      start: `${date}T${PLANT_CALENDAR.nightShiftStart}`,
      end: addMinutes(`${date}T00:00`, 24 * 60 + dayStart),
    };
  }

  const prev = addMinutes(`${date}T00:00`, -12 * 60);
  const prevDate = prev.split('T')[0]!;
  return {
    kind: 'night',
    start: `${prevDate}T${PLANT_CALENDAR.nightShiftStart}`,
    end: `${date}T${PLANT_CALENDAR.dayShiftStart}`,
  };
}

/** Production day runs night-then-day, labelled by the day-shift calendar date. */
export function productionDayOf(at: SeriesTime): {
  date: string;
  start: SeriesTime;
  end: SeriesTime;
} {
  const { kind } = shiftOf(at);
  const d = parseTime(at);
  const date = formatTime(d).split('T')[0]!;

  if (kind === 'day') {
    const prev = addMinutes(`${date}T00:00`, -12 * 60);
    const prevDate = prev.split('T')[0]!;
    return {
      date,
      start: `${prevDate}T${PLANT_CALENDAR.nightShiftStart}`,
      end: `${date}T${PLANT_CALENDAR.nightShiftStart}`,
    };
  }

  const hh = d.getUTCHours();
  if (hh >= 18) {
    const next = addMinutes(`${date}T00:00`, 24 * 60);
    const nextDate = next.split('T')[0]!;
    return {
      date: nextDate,
      start: `${date}T${PLANT_CALENDAR.nightShiftStart}`,
      end: `${nextDate}T${PLANT_CALENDAR.nightShiftStart}`,
    };
  }

  const prev = addMinutes(`${date}T00:00`, -12 * 60);
  const prevDate = prev.split('T')[0]!;
  return {
    date,
    start: `${prevDate}T${PLANT_CALENDAR.nightShiftStart}`,
    end: `${date}T${PLANT_CALENDAR.nightShiftStart}`,
  };
}

export type ShiftWindow = {
  kind: 'night' | 'day';
  start: SeriesTime;
  end: SeriesTime;
  /** e.g. "Night shift · 18:00–06:00". */
  label: string;
};

/** Both shift windows of `cursor`'s production day, always both present
 *  regardless of whether either shift has any data — the caller decides how
 *  to render an empty one (route-reads paul-feedback.A8 slices 3 and 5 share
 *  this: the daily report's per-shift decision record and the
 *  superintendent's per-role uptake read group by the same two windows). */
export function productionDayShiftWindows(cursor: SeriesTime): { night: ShiftWindow; day: ShiftWindow } {
  const { date, start: productionStart, end: productionEnd } = productionDayOf(cursor);
  const nightStart = productionStart;
  const nightEnd = `${date}T${PLANT_CALENDAR.dayShiftStart}` as SeriesTime;
  const dayStart = nightEnd;
  const dayEnd = productionEnd;
  const hm = (t: SeriesTime) => t.split('T')[1] ?? '';
  return {
    night: {
      kind: 'night',
      start: nightStart,
      end: nightEnd,
      label: `Night shift · ${hm(nightStart)}–${hm(nightEnd)}`,
    },
    day: {
      kind: 'day',
      start: dayStart,
      end: dayEnd,
      label: `Day shift · ${hm(dayStart)}–${hm(dayEnd)}`,
    },
  };
}

/** A bare `HH:MM` clock (seed convention for a same-day stamp, e.g. `Case.raisedAt`)
 *  stamped onto `cursor`'s production day; a clock that already carries a date
 *  passes through unchanged. Shared by `EffectivenessEngine` and the daily
 *  report's per-shift grouping (route-reads paul-feedback.A8 slice 3) — one
 *  reading of "which day does this bare clock belong to", not two. */
export function clockOnProductionDay(clock: string, cursor: SeriesTime): SeriesTime {
  const { date } = productionDayOf(cursor);
  if (clock.includes('T')) return clock;
  const hm = /^\d{2}:\d{2}$/.test(clock) ? clock : '06:00';
  return `${date}T${hm}`;
}

/**
 * Start of the ISO week window for daily series — Sunday 18:00 before the week's Monday,
 * so Monday's daily point (stamped Sun 18:00) is included.
 */
export function weekStart(at: SeriesTime): SeriesTime {
  const wd = isoWeekday(at);
  const { date } = productionDayOf(at);
  const prodDate = parseTime(`${date}T12:00`);
  prodDate.setUTCDate(prodDate.getUTCDate() - (wd - 1));
  const mon = formatTime(prodDate).split('T')[0]!;
  const sundayBeforeMonday = addMinutes(`${mon}T00:00`, -6 * 60);
  return `${sundayBeforeMonday.split('T')[0]!}T18:00`;
}

/**
 * Start of the calendar-month window for daily series — last day of the previous month at 18:00,
 * so the first-of-month daily point (stamped prior evening) is included.
 */
export function monthStart(at: SeriesTime): SeriesTime {
  const d = parseTime(at);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const lastPrev = new Date(Date.UTC(y, m, 0));
  const date = formatTime(lastPrev).split('T')[0]!;
  return `${date}T18:00`;
}

export function formatClock(at: SeriesTime): string {
  const d = parseTime(at);
  const day = d.getUTCDate();
  const mon = MONTHS[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const { kind, start, end } = shiftOf(at);
  const shiftLabel = kind === 'day' ? 'Day' : 'Night';
  const startHm = start.split('T')[1] ?? '06:00';
  const endHm = end.split('T')[1] ?? '18:00';
  const hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  return `${String(day).padStart(2, '0')} ${mon} ${year} · ${shiftLabel} ${startHm}–${endHm} · ${hm}`;
}

/** Category label for trend charts — e.g. `Sun 08` on the deck calendar. */
export function formatDayLabel(at: SeriesTime): string {
  const { date } = productionDayOf(at);
  const dom = Number.parseInt(date.split('-')[2]!, 10);
  const d = parseTime(`${date}T12:00`);
  if (dom >= 2 && dom <= 8) d.setUTCDate(d.getUTCDate() - 1);
  const name = DAY_NAMES[d.getUTCDay()];
  return `${name} ${String(dom).padStart(2, '0')}`;
}

/** Day and abbreviated month — `18 Jun`. Plant-local, `MONTHS` of this file.
 *  One formatter for every `DD Mon` label, so a window label and a date cell
 *  cannot drift apart. */
export function formatDayMonth(at: SeriesTime): string {
  const d = parseTime(at);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`;
}

/** Compact TopBar form — `08 Jun · Day shift · 11:30`; `formatClock` stays the long form. */
export function formatClockShort(at: SeriesTime): string {
  const d = parseTime(at);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  const { kind } = shiftOf(at);
  const hm = at.split('T')[1] ?? '00:00';
  return `${day} ${mon} · ${kind === 'day' ? 'Day' : 'Night'} shift · ${hm}`;
}

/** Cursor-bound footnote — `01 Jun 06:00` from `2026-06-01T06:00`. */
export function formatCursorBound(at: SeriesTime): string {
  const d = parseTime(at);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  const hm = at.split('T')[1] ?? '00:00';
  return `${day} ${mon} ${hm}`;
}

const MONTH_NUM: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

/** Compact month-day for ruling labels — `06-08` from seed formats or ISO. */
export function formatMonthDay(at: string, cursor: SeriesTime): string {
  if (!at) return '';
  if (at.includes('T')) {
    const d = parseTime(at as SeriesTime);
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${month}-${day}`;
  }
  const timeOnly = at.match(/^(\d{1,2}):(\d{2})$/);
  if (timeOnly) {
    const { date } = productionDayOf(cursor);
    return `${date.slice(5, 7)}-${date.slice(8, 10)}`;
  }
  const dayMonth = at.match(/(\d{1,2})\s+(\w{3})/);
  if (dayMonth) {
    const month = MONTH_NUM[dayMonth[2]!] ?? '06';
    const day = dayMonth[1]!.padStart(2, '0');
    return `${month}-${day}`;
  }
  return at.slice(5, 10);
}

/** On the cursor's production day → `HH:MM`; otherwise `DD Mon HH:MM`. */
export function formatCaseClock(stamp: string, cursor: SeriesTime): string {
  const full = stamp.includes('T') ? asCaseTime(stamp) : clockOnProductionDay(stamp, cursor);
  const prodDay = productionDayOf(cursor).date;
  const stampDay = full.split('T')[0]!;
  const hm = full.split('T')[1] ?? '00:00';
  if (stampDay === prodDay) return hm;
  const d = parseTime(full);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  return `${day} ${mon} ${hm}`;
}

/** The 12 h window ending at the current shift's start — the shift the reader
 *  just came off (vision-run cro-brief R1). */
export function previousShiftWindow(cursor: SeriesTime): ShiftWindow {
  const current = shiftOf(cursor);
  const end = current.start;
  const start = addMinutes(end, -12 * 60);
  const kind: ShiftWindow['kind'] = current.kind === 'day' ? 'night' : 'day';
  const hm = (t: SeriesTime) => t.split('T')[1] ?? '';
  return {
    kind,
    start,
    end,
    label: `${kind === 'night' ? 'Night' : 'Day'} shift · ${hm(start)}–${hm(end)}`,
  };
}

/** One shift earlier than `previousShiftWindow` — used when fewer than three
 *  brief lines fall in the first window (R1 minimum). */
export function shiftWindowBefore(window: ShiftWindow): ShiftWindow {
  const end = window.start;
  const start = addMinutes(end, -12 * 60);
  const kind: ShiftWindow['kind'] = window.kind === 'night' ? 'day' : 'night';
  const hm = (t: SeriesTime) => t.split('T')[1] ?? '';
  return {
    kind,
    start,
    end,
    label: `${kind === 'night' ? 'Night' : 'Day'} shift · ${hm(start)}–${hm(end)}`,
  };
}

/** Same production day and shift kind as `cursor`. */
export function isCaseThisShift(raisedAt: string, cursor: SeriesTime): boolean {
  if (!raisedAt.includes('T')) return false;
  const raised = asCaseTime(raisedAt);
  if (productionDayOf(raised).date !== productionDayOf(cursor).date) return false;
  return shiftOf(raised).kind === shiftOf(cursor).kind;
}

/** Raised in the same calendar month as `cursor`. */
export function isCaseThisMonth(raisedAt: string, cursor: SeriesTime): boolean {
  if (!raisedAt.includes('T')) return false;
  const d = parseTime(asCaseTime(raisedAt));
  const c = parseTime(cursor);
  return d.getUTCFullYear() === c.getUTCFullYear() && d.getUTCMonth() === c.getUTCMonth();
}

/** ISO calendar date from seed `raisedAt` strings (for vintage ordering). */
export function isoDateFromRaisedAt(raisedAt: string, cursor: SeriesTime): string {
  if (raisedAt.includes('T')) return raisedAt.slice(0, 10);
  const timeOnly = raisedAt.match(/^(\d{1,2}):(\d{2})$/);
  if (timeOnly) return productionDayOf(cursor).date;
  const dayMonth = raisedAt.match(/(\d{1,2})\s+(\w{3})/);
  if (dayMonth) {
    const month = MONTH_NUM[dayMonth[2]!] ?? '06';
    const day = dayMonth[1]!.padStart(2, '0');
    const year = productionDayOf(cursor).date.slice(0, 4);
    return `${year}-${month}-${day}`;
  }
  return raisedAt.slice(0, 10);
}
